import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import {
  EmailAuthProvider,
  GoogleAuthProvider,
  OAuthProvider,
  linkWithCredential,
  linkWithPopup,
  onAuthStateChanged,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut as fbSignOut,
  unlink,
  type User,
} from 'firebase/auth';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { auth, db } from './firebase';
import { useIdleSignOut } from './idleSignOut';
import type { ProfileEdits, UserProfile } from './types';

/** The sign-in methods a person can attach to, or detach from, one account. */
export type LinkableProvider = 'google.com' | 'apple.com';

function oauthProvider(id: LinkableProvider) {
  if (id === 'google.com') return new GoogleAuthProvider();
  const apple = new OAuthProvider('apple.com');
  apple.addScope('email');
  apple.addScope('name');
  return apple;
}

interface AuthValue {
  user: User | null;
  profile: UserProfile | null;
  loading: boolean;
  signInWithGoogle: () => Promise<void>;
  signInWithApple: () => Promise<void>;
  signInWithEmail: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  /**
   * Account linking. Each resolves to the provider ids now on the account, because
   * linking mutates `auth.currentUser` in place and fires no auth-state event — a caller
   * holding `user` would otherwise keep rendering the old list.
   */
  linkProvider: (id: LinkableProvider) => Promise<string[]>;
  unlinkProvider: (id: LinkableProvider) => Promise<string[]>;
  addPassword: (password: string) => Promise<string[]>;
  sendPasswordReset: () => Promise<void>;
  saveProfile: (edits: ProfileEdits) => Promise<void>;
}

const AuthContext = createContext<AuthValue | null>(null);

/**
 * Ensure `users/{uid}` exists.
 *
 * **Do not delete this.** It is load-bearing for every new account on the web.
 *
 * This used to say a `recordSignup` blocking function wrote the document server-side, and
 * that this copy was a stopgap for older accounts. There is no such function: the project
 * has 16 deployed functions and none is `recordSignup`, and Identity Platform has no
 * blocking triggers configured at all. Checked 2026-10-01.
 *
 * So nothing writes `users/{uid}` except a client. Some accounts have no profile document
 * — the Expo client never wrote one, and iOS only writes on a read-then-create that
 * evidently did not always complete.
 *
 * Merge, never overwrite: the phone clients own firstName/lastName once a user has edited
 * them, and a web sign-in must not reset that.
 */
async function ensureProfile(user: User): Promise<UserProfile | null> {
  const ref = doc(db, 'users', user.uid);
  const snap = await getDoc(ref);
  if (snap.exists()) {
    return snap.data() as UserProfile;
  }

  const [first = '', ...rest] = (user.displayName ?? '').trim().split(/\s+/);
  const created: UserProfile = {
    firstName: first,
    lastName: rest.join(' '),
    email: user.email ?? '',
  };
  await setDoc(ref, { ...created, updatedAt: serverTimestamp() }, { merge: true });
  return created;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    return onAuthStateChanged(auth, async (next) => {
      setUser(next);
      if (next) {
        try {
          setProfile(await ensureProfile(next));
        } catch {
          // A failed profile write must not block the session — the user is signed in
          // either way, and the notes query does not depend on this document.
          setProfile(null);
        }
      } else {
        setProfile(null);
      }
      setLoading(false);
    });
  }, []);

  // Here rather than in App.tsx, so it covers every signed-in screen — including Upgrade and
  // the entitlement-unavailable retry, which render before the routes exist.
  useIdleSignOut(user?.uid, !loading);

  const value = useMemo<AuthValue>(
    () => ({
      user,
      profile,
      loading,
      signInWithGoogle: async () => {
        await signInWithPopup(auth, new GoogleAuthProvider());
      },
      /**
       * Apple is configured on the project already — the provider is enabled with a client
       * id, and the Services ID's return URLs include this project's `__/auth/handler`,
       * which is where the popup lands whatever origin served the page.
       *
       * Two things about Apple worth knowing when reading a profile afterwards. The name
       * scope is only honoured on the *first* authorisation, so a user who signed in on the
       * phone before arrives here with no `displayName` and `ensureProfile` has nothing to
       * derive initials from. And Hide My Email yields a private relay address, so the email
       * on the account may not be one the person recognises.
       */
      signInWithApple: async () => {
        await signInWithPopup(auth, oauthProvider('apple.com'));
      },
      signInWithEmail: async (email, password) => {
        await signInWithEmailAndPassword(auth, email, password);
      },
      signOut: async () => {
        await fbSignOut(auth);
      },
      /*
       * Linking attaches an identity to the uid already signed in, so every note, the
       * profile document and the Pro entitlement (RevenueCat is keyed on the uid) follow
       * automatically — on all three clients, since they share this Firebase project.
       *
       * Doing it here, deliberately, is also safer than letting Firebase's same-email
       * matching do it at sign-in: a Google sign-in on an email whose password account is
       * unverified makes Google the sole provider and silently drops the password.
       */
      linkProvider: async (id) => {
        if (!auth.currentUser) throw new Error('Not signed in');
        const result = await linkWithPopup(auth.currentUser, oauthProvider(id));
        return result.user.providerData.map((p) => p.providerId);
      },
      unlinkProvider: async (id) => {
        const current = auth.currentUser;
        if (!current) throw new Error('Not signed in');
        // Firebase would allow removing the last method and leave an account nobody can
        // sign in to. The screen disables the button; this is the backstop.
        if (current.providerData.length < 2) {
          throw new Error('This is your only sign-in method, so it cannot be removed.');
        }
        const next = await unlink(current, id);
        return next.providerData.map((p) => p.providerId);
      },
      addPassword: async (password) => {
        const current = auth.currentUser;
        if (!current?.email) throw new Error('This account has no email address to sign in with.');
        const result = await linkWithCredential(
          current,
          EmailAuthProvider.credential(current.email, password)
        );
        return result.user.providerData.map((p) => p.providerId);
      },
      // Changing a password in place needs a recent sign-in, and with an hour's idle timeout
      // that is often not the case. A reset email works from any session.
      sendPasswordReset: async () => {
        if (!auth.currentUser?.email) throw new Error('This account has no email address.');
        await sendPasswordResetEmail(auth, auth.currentUser.email);
      },
      saveProfile: async (edits) => {
        if (!user) throw new Error('Not signed in');
        // Merge, always. This document also holds the iOS toast and voice-feedback
        // settings; a replacing write from here would wipe them.
        await setDoc(
          doc(db, 'users', user.uid),
          { ...edits, updatedAt: serverTimestamp() },
          { merge: true }
        );
        // Keep the context in step so the header initials update without a round trip.
        setProfile((current) => ({ ...current, ...edits }));
      },
    }),
    [user, profile, loading]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}
